// Subtitle language helpers for the CMS upload pickers.
//
// Manually-picked .srt files used to be labelled purely by the slot they were
// dropped into (slot 1 = English, slot 2 = Hindi), so a Spanish + Japanese pair
// was saved as English + Hindi. detectSubtitleLanguage() works out the real
// language from the filename or, failing that, from the subtitle text itself,
// and the pickers let the admin correct it via SUBTITLE_LANGUAGES.

// Codes match the backend's subtitleLanguageLabel map.
export const SUBTITLE_LANGUAGES = [
  { code: "en", name: "English" },
  { code: "hi", name: "Hindi" },
  { code: "es", name: "Spanish" },
  { code: "fr", name: "French" },
  { code: "de", name: "German" },
  { code: "it", name: "Italian" },
  { code: "pt", name: "Portuguese" },
  { code: "ar", name: "Arabic" },
  { code: "zh", name: "Chinese" },
  { code: "ja", name: "Japanese" },
  { code: "ko", name: "Korean" },
  { code: "ru", name: "Russian" },
  { code: "ta", name: "Tamil" },
  { code: "te", name: "Telugu" },
  { code: "mr", name: "Marathi" },
  { code: "bn", name: "Bengali" },
  { code: "gu", name: "Gujarati" },
  { code: "kn", name: "Kannada" },
  { code: "ml", name: "Malayalam" },
  { code: "pa", name: "Punjabi" },
  { code: "ur", name: "Urdu" },
  { code: "th", name: "Thai" },
  { code: "tr", name: "Turkish" },
  { code: "nl", name: "Dutch" },
  { code: "he", name: "Hebrew" },
];

// Filename tokens -> code. Covers ISO 639-1/-2 codes and English/native names,
// e.g. "movie.es.srt", "movie_spa.srt", "Japanese.srt", "español.srt".
const NAME_TOKENS = {
  en: ["en", "eng", "english"],
  hi: ["hi", "hin", "hindi"],
  es: ["es", "spa", "spanish", "espanol", "español", "castellano"],
  fr: ["fr", "fra", "fre", "french", "francais", "français"],
  de: ["de", "deu", "ger", "german", "deutsch"],
  it: ["it", "ita", "italian", "italiano"],
  pt: ["pt", "por", "portuguese", "portugues", "português", "ptbr"],
  ar: ["ar", "ara", "arabic"],
  zh: ["zh", "chi", "zho", "chinese", "mandarin", "chs", "cht"],
  ja: ["ja", "jp", "jpn", "japanese", "nihongo"],
  ko: ["ko", "kor", "korean"],
  ru: ["ru", "rus", "russian"],
  ta: ["ta", "tam", "tamil"],
  te: ["te", "tel", "telugu"],
  mr: ["mr", "mar", "marathi"],
  bn: ["bn", "ben", "bengali", "bangla"],
  gu: ["gu", "guj", "gujarati"],
  kn: ["kn", "kan", "kannada"],
  ml: ["ml", "mal", "malayalam"],
  pa: ["pa", "pan", "punjabi"],
  ur: ["ur", "urd", "urdu"],
  th: ["th", "tha", "thai"],
  tr: ["tr", "tur", "turkish"],
  nl: ["nl", "nld", "dut", "dutch"],
  he: ["he", "heb", "hebrew"],
};

// Two-letter codes are too easy to hit by accident ("it" in "watch-it"), so
// they only count in the conventional "name.xx.srt" position or as the whole
// name ("es.srt"); longer codes/names ("spa", "japanese") count anywhere.
export const languageFromFileName = (fileName) => {
  if (!fileName) return null;
  const base = String(fileName).toLowerCase().replace(/\.[^.]+$/, "");
  const tokens = base.split(/[\s._\-()[\]]+/).filter(Boolean);
  if (tokens.length === 0) return null;
  const last = tokens[tokens.length - 1];
  const lastIsTrustworthy = last.length > 2 || tokens.length === 1 || base.endsWith(`.${last}`);
  for (const [code, words] of Object.entries(NAME_TOKENS)) {
    if (lastIsTrustworthy && words.includes(last)) return code;
  }
  for (const [code, words] of Object.entries(NAME_TOKENS)) {
    if (tokens.some((t) => t.length > 2 && words.includes(t))) return code;
  }
  return null;
};

// Non-Latin scripts identify the language directly.
const SCRIPTS = [
  ["ja", /[぀-ヿ]/g], // Hiragana/Katakana (checked before Han)
  ["ko", /[가-힯]/g],
  ["zh", /[一-鿿]/g],
  ["hi", /[ऀ-ॿ]/g], // Devanagari (Hindi/Marathi share it; Hindi is the common case)
  ["bn", /[ঀ-৿]/g],
  ["pa", /[਀-੿]/g],
  ["gu", /[઀-૿]/g],
  ["ta", /[஀-௿]/g],
  ["te", /[ఀ-౿]/g],
  ["kn", /[ಀ-೿]/g],
  ["ml", /[ഀ-ൿ]/g],
  ["th", /[฀-๿]/g],
  ["ar", /[؀-ۿ]/g],
  ["he", /[֐-׿]/g],
  ["ru", /[Ѐ-ӿ]/g],
];

// Frequent short words for Latin-script languages.
const STOPWORDS = {
  en: ["the", "and", "you", "is", "to", "of", "it", "that", "what", "this", "i'm", "don't", "are", "was"],
  es: ["el", "la", "que", "de", "y", "es", "no", "en", "lo", "un", "por", "qué", "está", "los", "las", "pero", "sí"],
  fr: ["le", "la", "et", "est", "je", "vous", "pas", "que", "une", "les", "ce", "c'est", "qu'il", "oui"],
  de: ["der", "die", "und", "ist", "ich", "nicht", "das", "du", "sie", "ein", "es", "wir", "was", "ja"],
  it: ["il", "che", "di", "non", "è", "un", "per", "sono", "la", "ho", "mi", "cosa", "questo"],
  pt: ["o", "que", "não", "de", "é", "um", "uma", "você", "eu", "isso", "com", "está", "para"],
  nl: ["de", "het", "een", "en", "ik", "je", "niet", "is", "dat", "wat", "van", "we"],
  tr: ["bir", "bu", "ve", "ne", "ben", "sen", "değil", "için", "mi", "çok", "var"],
};

const decode = (buffer) => {
  const bytes = new Uint8Array(buffer);
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder("utf-16le").decode(bytes);
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder("utf-16be").decode(bytes);
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder("windows-1252").decode(bytes);
  }
};

export const languageFromSubtitleText = (rawText) => {
  if (!rawText) return null;
  // Keep only dialogue: drop cue indexes, timing lines, WEBVTT header, tags.
  const text = rawText
    .split(/\r?\n/)
    .filter((l) => l.trim() && !/^\d+\s*$/.test(l) && !l.includes("-->") && !/^WEBVTT/.test(l))
    .join("\n")
    .replace(/<[^>]*>|\{[^}]*\}/g, "");
  if (!text.trim()) return null;

  const letters = (text.match(/\p{L}/gu) || []).length || 1;
  for (const [code, re] of SCRIPTS) {
    const count = (text.match(re) || []).length;
    if (count / letters > 0.2) return code;
  }

  const words = text.toLowerCase().match(/[\p{L}']+/gu) || [];
  let best = null;
  let bestScore = 0;
  for (const [code, list] of Object.entries(STOPWORDS)) {
    const set = new Set(list);
    const score = words.reduce((n, w) => n + (set.has(w) ? 1 : 0), 0);
    if (score > bestScore) {
      best = code;
      bestScore = score;
    }
  }
  // Need a reasonable signal before trusting a Latin-script guess.
  return bestScore >= 3 ? best : null;
};

/** Best-effort language code for a picked subtitle File, or null if unsure. */
export const detectSubtitleLanguage = async (file) => {
  const fromName = languageFromFileName(file?.name);
  if (fromName) return fromName;
  try {
    // The first ~64KB is plenty of dialogue to classify.
    const buffer = await file.slice(0, 64 * 1024).arrayBuffer();
    return languageFromSubtitleText(decode(buffer));
  } catch {
    return null;
  }
};

export const subtitleLanguageName = (code) => {
  const c = String(code || "").toLowerCase();
  return SUBTITLE_LANGUAGES.find((l) => l.code === c)?.name || c.toUpperCase();
};
